import snakecaseKeys from 'snakecase-keys';
import { UserRole, UserRules } from '@uplift/types';
import * as batshit from '@yornaath/batshit';

import { restClient } from '../../client';
import { ProviderDetail, User, UserAdminResource, UserProfile } from '../../resources';
import { Asset } from '../../resources/asset';
import { HigherEdManagerDetails, HigherEdStudentDetails } from '../higherEd';


export type AdminUserResource = {
  id: string;
  role: UserRole.provider;
  user: User;
  profile: UserProfile;
  avatarAsset?: Asset;
  providerDetails: ProviderDetail;
  rules: UserRules;
} | {
  id: string;
  role: UserRole.patient;
  user: User;
  profile: UserProfile;
  avatarAsset?: Asset;
  providerDetails: null;
  rules: UserRules;
} | {
  id: string;
  role: UserRole.superadmin;
  user: User;
  profile: UserProfile | null;
  avatarAsset?: Asset;
  providerDetails: null;
  rules: null;
};

export const adminGetUsersByIdRequest = (userIds: string[]) => restClient.request<{ data: AdminUserResource[] }>('/api/v1/admin/users', {
  method: 'POST',
  data: {
    user_ids: userIds,
  }
});


export const adminUserByIdBatch = batshit.create({
  fetcher: async (userIds: string[]) => {
    const response = await adminGetUsersByIdRequest(userIds.filter(Boolean));

    return response.data;
  },
  resolver: batshit.keyResolver('id'),
  scheduler: batshit.windowScheduler(10)
});


export const adminUserRequest = (userId: string) => adminUserByIdBatch.fetch(userId);


export const adminTaskExecutionGetAll = () => restClient.request<{
  data: {
    name: string;
    description: string;
  }[];
}>('/api/v1/admin/task_execution');

export const adminTaskExecutionExecute = (taskName: string) => restClient.request<{
  data: {
    result: string;
  };
}>('/api/v1/admin/task_execution', {
  method: 'POST',
  data: {
    name: taskName
  }
});


export const adminGetAdminUsers = () => restClient.request<{ data: UserAdminResource[] }>('/api/v1/admin/admin_users');
export type AddAdminParams = Pick<UserAdminResource, 'email' | 'firstName' | 'lastName' | 'adminRole' | 'active'>;
export const adminAddAdminUser = (adminUser: AddAdminParams) => restClient.request<{ data: UserAdminResource[] }>('/api/v1/admin/admin_users', {
  method: 'POST',
  data: snakecaseKeys(adminUser)
});


export type UpdateAdminParams = Pick<UserAdminResource, 'id' | 'email' | 'firstName' | 'lastName' | 'adminRole' | 'active'>;
export const adminUpdateAdminUser = (adminUser: UpdateAdminParams) => restClient.request<{ data: UserAdminResource[] }>(`/api/v1/admin/admin_users/${adminUser.id}`, {
  method: 'PATCH',
  data: snakecaseKeys(adminUser)
});


export const adminSetUserActive = (params: {
  user_id: string;
  active: boolean;
  from_datetime?: string;
}) => restClient.request<null>('/api/v1/admin/set_user_active_status', {
  method: 'POST',
  data: params,
});

export type AdminSearchInput = {
  search_phrase: string;
};

export type AdminUsersOutput = {
  users: UserAdminResource[];
  providerDetails: ProviderDetail[];
  markedForNonpayment?: string[];
  higherEdManagerDetails?: HigherEdManagerDetails[];
  higherEdStudentDetails?: HigherEdStudentDetails[];
};

export const adminSearchUsersRequest = (params: AdminSearchInput) => restClient.request<AdminUsersOutput>('/api/v1/admin/search_users_and_details', {
  method: 'POST',
  data: params,
});

export const higherEdAdminSearchManagersRequest = (params: AdminSearchInput) => restClient.request<Omit<AdminUsersOutput, 'higherEdStudentDetails'>>('/api/v1/search/higher_ed/org_managers', {
  method: 'POST',
  data: params,
});

export type PatchProviderMSOsAndGroupsInput = {
  mso_partner_ids?: string[];
  mso_group_ids?: string[];
};

export const patchProviderMsoAndGroups = (providerId: string, data: PatchProviderMSOsAndGroupsInput) =>
  restClient.request(`/api/v1/admin/mso/${providerId}`, {
    method: 'PATCH',
    data: data
  });
